import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import Card from "components/Card";
import Button from "components/Button";
import Table from "components/Table";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import {
  fetchClientUserDetailAPI,
  getClientUserAssignmentsAPI,
  createEmployeeClientAssignmentAPI,
  deleteEmployeeClientAssignmentAPI,
  getEmployees,
} from "api/apiHelper";
import type { ClientAssignment } from "types/client-management-types";
import type { EmployeeType } from "types/leaves-types";
import { toast } from "react-toastify";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface ClientUserDetail {
  id: number;
  client_name: string;
  client_id: number;
  user_id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  job_title: string | null;
  photo: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const ClientUserDetail = () => {
  const { clientUserId } = useParams<{ clientUserId: string }>();
  const navigate = useNavigate();
  const [clientUser, setClientUser] = useState<ClientUserDetail | null>(null);
  const [assignments, setAssignments] = useState<ClientAssignment[]>([]);
  const [paginationData, setPaginationData] = useState<PaginatedResponse<ClientAssignment> | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [assignmentsLoading, setAssignmentsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false);
  const [employees, setEmployees] = useState<EmployeeType[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [assigning, setAssigning] = useState<boolean>(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const columnHelper = createColumnHelper<ClientAssignment>();
  const fetchClientUserDetail = async () => {
    if (!clientUserId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await fetchClientUserDetailAPI(parseInt(clientUserId, 10));
      setClientUser(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load client user information.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (size: number = pageSize, pageUrl?: string) => {
    if (!clientUserId) return;

    try {
      setAssignmentsLoading(true);
      setError(null);
      const response = await getClientUserAssignmentsAPI(parseInt(clientUserId, 10), size, pageUrl);

      if (response.data?.results) {
        setPaginationData(response.data);
        setAssignments(response.data.results);
      } else if (Array.isArray(response.data)) {
        setAssignments(response.data);
        setPaginationData(null);
      } else {
        setAssignments([]);
        setPaginationData(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load assigned employees.");
      setAssignments([]);
      setPaginationData(null);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const handleNextPage = () => {
    if (paginationData?.next) {
      fetchAssignments(pageSize, paginationData.next);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (paginationData?.previous) {
      fetchAssignments(pageSize, paginationData.previous);
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handlePageSizeChange = async (newSize: number) => {
    setPageSize(newSize);
    await fetchAssignments(newSize);
  };

  const getAssignedEmployeeIds = (): Set<number> => {
    const assignedIds = new Set<number>();
    assignments.forEach((assignment) => {
      if (assignment.employee_id) {
        assignedIds.add(assignment.employee_id);
      } else if (assignment.employee) {
        assignedIds.add(assignment.employee);
      }
    });
    return assignedIds;
  };

  const openAssignModal = async () => {
    setAssignError(null);
    setSelectedEmployeeId("");
    setAssignModalOpen(true);

    try {
      const response = await getEmployees(true);
      const employeesList = Array.isArray(response.data) 
        ? response.data 
        : response.data?.results || [];
      setEmployees(employeesList as EmployeeType[]);
    } catch (err: any) {
      setAssignError(err?.response?.data?.error || err.message || "Failed to load employees.");
    }
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssignError(null);
    setSelectedEmployeeId("");
  };

  const handleAssign = async () => {
    if (!clientUserId || !selectedEmployeeId) return;

    const selectedId = parseInt(selectedEmployeeId, 10);
    const assignedIds = getAssignedEmployeeIds();
    
    // Validate that employee is not already assigned
    if (assignedIds.has(selectedId)) {
      const selectedEmployee = employees.find((emp) => parseInt(emp.id, 10) === selectedId);
      const employeeName = selectedEmployee 
        ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}`
        : "This employee";
      setAssignError(`${employeeName} is already assigned to this client user.`);
      toast.error(`${employeeName} is already assigned.`);
      return;
    }

    try {
      setAssigning(true);
      setAssignError(null);
      await createEmployeeClientAssignmentAPI(selectedId, parseInt(clientUserId, 10));
      toast.success("Employee assigned successfully!");
      closeAssignModal();
      await fetchAssignments(pageSize);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.response?.data?.non_field_errors?.[0] || err.message || "Failed to assign employee.";
      setAssignError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setAssigning(false);
    }
  };

  const openDeleteModal = (assignmentId: number) => {
    setAssignmentToDelete(assignmentId);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteAssignment = async () => {
    if (!assignmentToDelete) return;

    try {
      setDeleting(true);
      await deleteEmployeeClientAssignmentAPI(assignmentToDelete);
      toast.success("Employee assignment removed successfully!");
      closeDeleteModal();
      await fetchAssignments(pageSize);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err.message || "Failed to remove employee assignment.";
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (clientUserId) {
      fetchClientUserDetail();
      fetchAssignments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUserId]);

  const columns: ColumnDef<ClientAssignment, any>[] = [
    columnHelper.accessor("employee_name", {
      id: "employee_name",
      header: () => <TableHeader>Employee</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="font-medium text-gray-900 dark:text-white">{info.getValue()}</span>
        </TableCell>
      ),
    }),
    columnHelper.accessor("employee_email", {
      id: "employee_email",
      header: () => <TableHeader>Email</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="text-gray-700 dark:text-gray-300">{info.getValue()}</span>
        </TableCell>
      ),
    }),
    columnHelper.accessor("client_name", {
      id: "client_name",
      header: () => <TableHeader>Client</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="text-gray-700 dark:text-gray-300">{info.getValue()}</span>
        </TableCell>
      ),
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <TableHeader>Assigned On</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="text-gray-500 dark:text-gray-400">
            {new Date(info.getValue()).toLocaleDateString()}
          </span>
        </TableCell>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => <TableHeader>Actions</TableHeader>,
      cell: (info) => (
        <TableCell className="!min-w-0 !pr-0 !w-max">
          <button
            onClick={() => openDeleteModal(info.row.original.id)}
            className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
            title="Remove Assignment"
          >
            <i className="fas fa-trash text-red-600 dark:text-red-400 text-sm"></i>
          </button>
        </TableCell>
      ),
    }),
  ];

  return (
    <div className="mt-5 space-y-6">
      {error && <ErrorBox errorMessage={error} />}

      {/* Client User Information Card */}
      {clientUser && (
        <Card extra="w-full h-full p-6 bg-white dark:bg-navy-800 rounded-xl shadow-sm">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {clientUser.photo ? (
                <img
                  src={clientUser.photo}
                  alt={`${clientUser.first_name} ${clientUser.last_name}`}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-navy-700 flex items-center justify-center">
                  <i className="fas fa-user text-3xl text-gray-400"></i>
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
                  {clientUser.first_name} {clientUser.last_name}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{clientUser.email}</p>
              </div>
            </div>
            <Button
              onClick={openAssignModal}
              className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium flex items-center gap-2 dark:bg-brand-400 dark:hover:bg-brand-500"
            >
              <i className="fas fa-user-plus"></i>
              Assign Employee
            </Button>
          </div>

          <div className="mt-2 mb-6 w-full">
            <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white">General Information</h4>
            <div className="mt-2 h-px w-full bg-gray-200 dark:bg-navy-700"></div>
          </div>

          {/* Client Information Section */}
          <div className="mb-6">
            <h5 className="mb-4 px-2 text-base font-semibold text-gray-700 dark:text-white">
              <i className="fas fa-building mr-2 text-blue-600 dark:text-blue-400"></i>
              Client Information
            </h5>

            <div className="grid grid-cols-1 gap-4 px-2 md:grid-cols-2">
              <div className="rounded-lg bg-white bg-clip-border px-3 py-4 shadow-sm dark:!bg-navy-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">Client Name</p>
                <p className="text-base font-medium text-navy-700 dark:text-white">
                  {clientUser.client_name}
                </p>
              </div>
            </div>
          </div>

          {/* User Account Details Section */}
          <div className="mb-6">
            <h5 className="mb-4 px-2 text-base font-semibold text-gray-700 dark:text-white">
              <i className="fas fa-user-circle mr-2 text-blue-600 dark:text-blue-400"></i>
              User Account Details
            </h5>

            <div className="grid grid-cols-1 gap-4 px-2 md:grid-cols-2">
              <div className="rounded-lg bg-white bg-clip-border px-3 py-4 shadow-sm dark:!bg-navy-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">Username</p>
                <p className="text-base font-medium text-navy-700 dark:text-white">
                  {clientUser.username}
                </p>
              </div>

              <div className="rounded-lg bg-white bg-clip-border px-3 py-4 shadow-sm dark:!bg-navy-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                <p className="text-base font-medium text-navy-700 dark:text-white">
                  {clientUser.email}
                </p>
              </div>
            </div>
          </div>

          {/* Professional Details Section */}
          <div>
            <h5 className="mb-4 px-2 text-base font-semibold text-gray-700 dark:text-white">
              <i className="fas fa-briefcase mr-2 text-blue-600 dark:text-blue-400"></i>
              Professional Details
            </h5>

            <div className="grid grid-cols-1 gap-4 px-2 md:grid-cols-2">
              <div className="rounded-lg bg-white bg-clip-border px-3 py-4 shadow-sm dark:!bg-navy-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">Job Title</p>
                <p className="text-base font-medium text-navy-700 dark:text-white">
                  {clientUser.job_title || "N/A"}
                </p>
              </div>

              <div className="rounded-lg bg-white bg-clip-border px-3 py-4 shadow-sm dark:!bg-navy-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                    clientUser.is_active
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {clientUser.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Assigned Employees Table */}
      <div className="mt-6">
        {assignmentsLoading && !assignments.length ? (
          <Backdrop show={true} />
        ) : (
          <Table<ClientAssignment>
            tableData={assignments}
            columns={columns}
            tableHeading="Assigned Employees"
            totalCount={paginationData?.count || 0}
            currentPage={currentPage}
            pageSize={pageSize}
            hasNextPage={!!paginationData?.next}
            hasPreviousPage={!!paginationData?.previous}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>

      {/* Assign Employee Modal */}
      {assignModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={closeAssignModal}
        >
          <div 
            className="bg-white dark:bg-navy-800 rounded-xl shadow-xl w-full max-w-lg p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeAssignModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Close"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4 pr-8">
              Assign Employee
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Assign an employee to{" "}
              <span className="font-semibold">
                {clientUser?.first_name} {clientUser?.last_name} ({clientUser?.client_name})
              </span>
              .
            </p>

            {assignError && <ErrorBox errorMessage={assignError} />}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Select Employee
              </label>
              <select
                value={selectedEmployeeId}
                onChange={(e) => {
                  setSelectedEmployeeId(e.target.value);
                  setAssignError(null); // Clear error when selection changes
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">Choose an employee...</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={closeAssignModal}
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-navy-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssign}
                disabled={!selectedEmployeeId || assigning}
                className={`px-4 py-2 rounded-md text-white font-semibold ${
                  !selectedEmployeeId || assigning
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500"
                }`}
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={closeDeleteModal}
        >
          <div 
            className="bg-white dark:bg-navy-800 rounded-xl shadow-xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeDeleteModal}
              disabled={deleting}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Close"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
            <div className="flex items-center gap-4 mb-4 pr-8">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                Remove Assignment
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to remove this employee assignment? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleting}
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-navy-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAssignment}
                disabled={deleting}
                className={`px-4 py-2 rounded-md text-white font-semibold ${
                  deleting
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700"
                }`}
              >
                {deleting ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientUserDetail;
